/**
 * Domains Hooks
 *
 * TanStack Query-backed hooks for domains. Reads share the `['domains']`
 * cache; mutations invalidate it (and `['sites']` on delete, since a domain
 * may have been attached to a site).
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import {
  domainsApi,
  Domain,
  CreateDomainRequest,
  CreateDomainResponse,
  DomainVerification,
} from '@/api/domainsApi';
import { DEFAULT_PER_PAGE, type Paginated, type PaginationParams } from '@/api/pagination';

/**
 * Hook for fetching domains for the organization (paginated).
 *
 * Backed by `['domains', { page, perPage }]`. Gated on auth readiness so the
 * hook does not fire during the cold-load window (mirrors `useSites`).
 */
export function useDomains(params: PaginationParams = {}) {
  const { isAuthenticated, isSdkReady } = useAuth();
  const page = params.page ?? 1;
  const perPage = params.perPage ?? DEFAULT_PER_PAGE;

  return useQuery<Paginated<Domain>>({
    queryKey: ['domains', { page, perPage }],
    queryFn: () => domainsApi.getDomains({ page, perPage }),
    enabled: isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook for creating a custom domain.
 *
 * Invalidates `['domains']` on success.
 */
export function useCreateDomain() {
  const queryClient = useQueryClient();
  return useMutation<CreateDomainResponse, Error, CreateDomainRequest>({
    mutationFn: (request) => domainsApi.createDomain(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
    },
  });
}

/**
 * Hook for verifying a domain's DNS configuration.
 *
 * Invalidates `['domains']` on success — verification can flip the
 * `verified`/`routingConfigured`/`sslStatus` fields the list reads.
 */
export function useVerifyDomain() {
  const queryClient = useQueryClient();
  return useMutation<DomainVerification, Error, string>({
    mutationFn: (domainId) => domainsApi.verifyDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
    },
  });
}

/**
 * Hook for deleting a domain.
 *
 * Invalidates `['domains']` and `['sites']` on success — a deleted domain
 * may have been attached to a site, whose detail row surfaces domain state.
 */
export function useDeleteDomain() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (domainId) => domainsApi.deleteDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}
